import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';

interface RoomState {
  id: string;
  players: string[];
  gameState: 'waiting' | 'countdown' | 'playing' | 'results';
  countdown: number;
  playerChoices: Record<string, 'rock' | 'paper' | 'scissors' | null>;
  winner: string | null;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState('');

  const createRoom = () => {
    const newRoomId = Math.random().toString(36).substring(2, 8);
    navigate(`/room/${newRoomId}`);
  };

  const joinRoom = () => {
    if (roomId.trim()) {
      navigate(`/room/${roomId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md w-96 text-center">
        <h1 className="text-2xl font-bold mb-6">Rock Paper Scissors</h1>
        <div className="space-y-4">
          <button
            type="button"
            onClick={createRoom}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition"
          >
            Create Room
          </button>
          <div className="flex space-x-2">
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="Room ID"
              className="flex-1 px-3 py-2 border rounded"
            />
            <button
              type="button"
              onClick={joinRoom}
              className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition"
            >
              Join
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const [roomState, setRoomState] = useState<RoomState>({
    id: roomId || '',
    players: [],
    gameState: 'waiting',
    countdown: 5,
    playerChoices: {},
    winner: null
  });
  const [playerName] = useState('Player ' + Math.floor(Math.random() * 1000));
  const [ws, setWs] = useState<WebSocket | null>(null);

  useEffect(() => {
    if (!roomId) return;

    // Connect to WebSocket
    const socket = new WebSocket(`wss://rock-paper-scissors.lotrez.partykit.dev/parties/${roomId}`);
    setWs(socket);

    socket.onopen = () => {
      console.log('Connected to room:', roomId);
      socket.send(JSON.stringify({
        type: 'join',
        playerName: playerName,
        roomId: roomId
      }));
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received:', data);
      
      if (data.type === 'roomState') {
        setRoomState(data.roomState);
      }
    };

    socket.onclose = () => {
      console.log('Disconnected from room:', roomId);
    };

    return () => {
      socket.close();
    };
  }, [roomId, playerName]);

  const makeChoice = (choice: 'rock' | 'paper' | 'scissors') => {
    if (ws && roomState.gameState === 'playing') {
      ws.send(JSON.stringify({
        type: 'choice',
        playerName: playerName,
        choice: choice
      }));
    }
  };

  const startGame = () => {
    if (ws && roomState.players.length >= 2) {
      ws.send(JSON.stringify({
        type: 'startGame',
        playerName: playerName
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Room: {roomId}</h2>
        
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Players ({roomState.players.length}/2):</h3>
          <ul className="space-y-1">
            {roomState.players.map((player) => (
              <li key={player} className="flex items-center">
                <span className={`w-2 h-2 ${player === playerName ? 'bg-blue-500' : 'bg-green-500'} rounded-full mr-2`}></span>
                {player}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">You are: <span className="font-medium">{playerName}</span></p>
        </div>

        {roomState.gameState === 'waiting' && (
          <div className="text-center">
            <p className="text-gray-600 mb-4">Waiting for players...</p>
            {roomState.players.length >= 2 && (
            <button
              type="button"
              onClick={startGame}
              className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition"
            >
              Start Game
            </button>
            )}
          </div>
        )}

        {roomState.gameState === 'countdown' && (
          <div className="text-center">
            <p className="text-xl font-bold mb-4">Game starting in: {roomState.countdown}</p>
          </div>
        )}

        {roomState.gameState === 'playing' && (
          <div className="text-center">
            <p className="text-gray-600 mb-4">Make your choice!</p>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={() => makeChoice('rock')}
                className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition"
              >
                🪨
              </button>
              <button
                type="button"
                onClick={() => makeChoice('paper')}
                className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition"
              >
                📄
              </button>
              <button
                type="button"
                onClick={() => makeChoice('scissors')}
                className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition"
              >
                ✂️
              </button>
            </div>
          </div>
        )}

        {roomState.gameState === 'results' && (
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Results:</h3>
            {roomState.winner ? (
              <p className="text-green-600 font-medium">Winner: {roomState.winner}</p>
            ) : (
              <p className="text-gray-600">It's a tie!</p>
            )}
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Choices:</p>
              {Object.entries(roomState.playerChoices).map(([player, choice]) => (
                <p key={player} className="text-sm">
                  {player}: {choice || 'No choice'}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="" element={<HomePage />} />
        <Route path="/room/:roomId" element={<RoomPage />} />
      </Routes>
    </Router>
  );
};

export default App;